/**
 * LeadGenProgressBar - Modern minimal progress indicator for lead generation
 * Clean dark design with smooth animations matching updated UI schema
 */
import React from 'react';
import { LeadGenProgress } from '../../types';
import { CheckCircle, Target } from 'lucide-react';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface ProgressBarProps {
  progressData: LeadGenProgress;
  className?: string;
  tableType?: 'companies' | 'people' | 'custom';
}

// Estimate remaining time based on progress and analyzed count
const estimateTimeRemaining = (completion: number, analyzed: number): string => {
  if (completion >= 95) return 'Almost done';
  if (completion < 5) return 'Starting up';

  // Rough estimation: ~1 second per 2-3 analyzed items on average
  const remaining = 100 - completion;
  const rate = analyzed / completion; // items per percent
  const estimatedRemaining = rate * remaining;
  const estimatedSeconds = estimatedRemaining * 0.4; // ~0.4 seconds per item

  if (estimatedSeconds < 30) return '< 1 min';
  if (estimatedSeconds < 90) return '~1 min';
  if (estimatedSeconds < 180) return '~2 min';
  if (estimatedSeconds < 300) return '~3-5 min';
  return 'A few min';
};

export const LeadGenProgressBar: React.FC<ProgressBarProps> = ({ progressData, className = '', tableType = 'companies' }) => {
  const { completion, found, analyzed } = progressData;

  // Dynamic label based on table type
  const entityLabel = tableType === 'people' ? 'people' : tableType === 'custom' ? 'results' : 'companies';

  // Estimate time remaining
  const timeEstimate = estimateTimeRemaining(completion, analyzed);

  return (
    <div className={`${className}`}>
      {/* Main container - dark minimal design, full width above column headers */}
      <div
        className="bg-gray-900 shadow-lg overflow-hidden"
        style={{
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
        }}
      >
        {/* Top section - logo, message, stats */}
        <div className="px-4 py-3.5 flex items-center gap-3">
          {/* Skylarq logo with pulse animation */}
          <div className="relative flex-shrink-0">
            <img
              src={skylarqLogo}
              alt=""
              className="w-8 h-8 object-contain"
              style={{ filter: 'brightness(1.1)' }}
            />
            {/* Pulse ring animation */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                animation: 'progress-pulse 2s ease-in-out infinite',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}
            />
          </div>

          {/* Message and time */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-white">
                Finding {entityLabel}
              </span>
              {/* Animated dots */}
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-white rounded-full" style={{ animation: 'progress-dot 1.4s ease-in-out infinite' }} />
                <span className="w-1 h-1 bg-white rounded-full" style={{ animation: 'progress-dot 1.4s ease-in-out 0.2s infinite' }} />
                <span className="w-1 h-1 bg-white rounded-full" style={{ animation: 'progress-dot 1.4s ease-in-out 0.4s infinite' }} />
              </span>
            </div>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Scanning public sources for your criteria
            </p>
          </div>

          {/* Time estimate badge */}
          <div className="flex-shrink-0">
            <div className="px-2.5 py-1 rounded-full bg-white/10 text-[11px] font-medium text-gray-300">
              {timeEstimate}
            </div>
          </div>
        </div>

        {/* Progress bar - animated gradient */}
        <div className="h-1 bg-gray-800 relative overflow-hidden">
          {/* Actual progress */}
          <div
            className="absolute inset-y-0 left-0 transition-all duration-500 ease-out"
            style={{
              width: `${Math.round(completion)}%`,
              background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)'
            }}
          />
          {/* Shimmer overlay */}
          <div
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: 'progress-shimmer 1.5s ease-in-out infinite'
            }}
          />
        </div>

        {/* Stats row - clean minimal */}
        <div className="px-4 py-2.5 flex items-center justify-between">
          {/* Left stats */}
          <div className="flex items-center gap-4">
            {/* Found count */}
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2.5} />
              <span className="text-[12px] text-gray-400">
                <span className="font-semibold text-white">{found}</span> found
              </span>
            </div>
            {/* Analyzed count */}
            <div className="flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
              <span className="text-[12px] text-gray-400">
                <span className="font-medium text-gray-300">{analyzed}</span> analyzed
              </span>
            </div>
          </div>

          {/* Progress percentage */}
          <div className="text-[12px] font-semibold text-white">
            {Math.round(completion)}%
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes progress-pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          50% {
            transform: scale(1.15);
            opacity: 0;
          }
        }

        @keyframes progress-dot {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes progress-shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>
    </div>
  );
};
